import {
  Badge,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useReduxHook } from "../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";

import {
  AddRounded,
  DeleteOutlineRounded,
  DownloadOutlined,
  DownloadRounded,
  EastOutlined,
  EditRounded,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  FilterAltOutlined,
  ForkRightOutlined,
  HourglassEmptyOutlined,
  InputOutlined,
  KeyboardArrowDown,
  SaveOutlined,
  SettingsOutlined,
  StarOutlineRounded,
  StarRounded,
  SubdirectoryArrowRight,
  SwapVertOutlined,
  TableRowsOutlined,
  UpdateRounded,
  ViewComfyOutlined,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import { isBefore } from "date-fns";
import { isEmpty, isNil, size } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import CellRendererEditableLoanField from "../../_src_shared/agGrid/cellRenderer/CellRendererEditableLoanField";
import { columnTypesDate } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesDollar } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import { columnTypesNumber } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import CellRendererLoanComments from "../../components/agGrid/cellRenderer/CellRendererLoanComments";
import CellRendererLoanName from "../../components/agGrid/cellRenderer/CellRendererLoanName";
import CellRendererStackedChip from "../../components/agGrid/cellRenderer/CellRendererStackedChip";
import CellRendererUserByEmail from "../../components/agGrid/cellRenderer/CellRendererUserByEmail";
import ButtonMenu from "../../components/ButtonMenu";
import SearchInput from "../../components/inputs/SearchInput";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import Flx from "../../components/layout/Flx";
import ScreenContent from "../../components/layout/ScreenContent";
import BasicModal from "../../components/modals/BasicModal";
import FilterTabSwitcher from "../../components/navigation/FilterTabSwitcher";
import StyledMenu from "../../components/StyledMenu";
import Txt from "../../components/typography/Txt";
import OpenInNewTabButton from "../../components/ui/buttons/OpenInNewTabButton";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { useUserPreferences } from "../../hooks/utils/useUserPreferences";
import CreateLoanButton from "../LoanPipelineScreen/buttons/CreateLoanButton";

// ─── Constants ────────────────────────────────────────────────────────────────

const PIPELINE_NAME_TO_TITLE_MAP = {
  all: "All Loans",
  underwriting: "Underwriting Pipeline",
  scenarios: "Scenario Pipeline",
  "borrower-portal-scenarios": "Borrower Portal Scenarios Pipeline",
  favorites: "Favorite Loans",
};
const LOAN_STATUS_OPTIONS = [
  "Prospect",
  "Terms Accepted",
  "Processing",
  "Underwriting",
  "Condition Clearance",
  "Final Review",
  "Clear To Close",
  "Closed",
  "Inactive",
  "Dead",
];

const LOAN_SUBSTATUS_MAP = {
  Processing: ["Appraisal Ordered", "Appraisal In", "Pre-Underwrite"],
  "Clear To Close": ["Docs Out", "Docs Back"],
  Closed: ["Funded with Post Close Conditions", "Funded"],
};

const SUBSTATUS_TO_PARENT_MAP = Object.entries(LOAN_SUBSTATUS_MAP).reduce(
  (acc, [parent, subs]) => {
    subs.forEach((sub) => {
      acc[sub] = parent;
    });
    return acc;
  },
  {},
);

const SORT_OPTIONS = [
  { label: "Created Date (Newest)", field: "createDateTime", sort: "desc" },
  { label: "Created Date (Oldest)", field: "createDateTime", sort: "asc" },
  { label: "Recently Updated (Newest)", field: "updateDateTime", sort: "desc" },
  { label: "Recently Updated (Oldest)", field: "updateDateTime", sort: "asc" },
  {
    label: "Recently Added Comments (Newest)",
    field: "comments",
    sort: "desc",
  },
  { label: "Recently Added Comments (Oldest)", field: "comments", sort: "asc" },
];

const VIEW_OPTIONS = [
  { label: "All Loans", value: "all" },
  { label: "Only My Loans", value: "assigned" },
  { label: "Loan Favorites", value: "favorites" },
];

const SORT_MENU_OPTIONS = SORT_OPTIONS.map((opt) => ({
  label: opt.label,
  value: `${opt.field}-${opt.sort}`,
}));

// ─── Safe Type Helpers ────────────────────────────────────────────────────────
// These guard against Python "None", "null", "undefined", wrong types, etc.

/** Returns true if a raw string value is a garbage/sentinel value */
const isGarbageValue = (val) =>
  val === null ||
  val === undefined ||
  val === "None" ||
  val === "null" ||
  val === "undefined" ||
  val === "";

/** Safely parse a JSON string that should be an array. Always returns []. */
const safeParseArray = (raw) => {
  if (isGarbageValue(raw)) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Safely read a string from localStorage. Returns null for garbage values. */
const safeReadString = (raw) => {
  if (isGarbageValue(raw)) return null;
  return typeof raw === "string" ? raw : null;
};

/** Safely read a boolean-as-string from localStorage. Returns false for garbage values. */
const safeReadBool = (raw) => {
  if (isGarbageValue(raw)) return false;
  return raw === "true";
};

// ─── Premade Layout Helpers ───────────────────────────────────────────────────

/** Sanitize a single column state value — converts Python "None" to null */
const sanitizeColumnStateValue = (val) => (val === "None" ? null : val);

/** Sanitize a premade layout object so all Python "None" strings become null */
const sanitizePremadeLayout = (layout) => ({
  ...layout,
  activeSort: layout.activeSort === "None" ? null : layout.activeSort,
  filters: {
    ...layout.filters,
    statusFilter:
      layout.filters?.statusFilter === "None"
        ? null
        : (layout.filters?.statusFilter ?? null),
    subStatusFilter:
      layout.filters?.subStatusFilter === "None"
        ? null
        : (layout.filters?.subStatusFilter ?? null),
    quickFilter:
      layout.filters?.quickFilter === "None"
        ? null
        : (layout.filters?.quickFilter ?? null),
    activeView: layout.filters?.activeView ?? "all",
  },
  columnState: layout.columnState?.map((col) => ({
    ...col,
    aggFunc: sanitizeColumnStateValue(col.aggFunc),
    sort: sanitizeColumnStateValue(col.sort),
    sortIndex: sanitizeColumnStateValue(col.sortIndex),
    pinned: sanitizeColumnStateValue(col.pinned),
    pivotIndex: sanitizeColumnStateValue(col.pivotIndex),
    rowGroupIndex: sanitizeColumnStateValue(col.rowGroupIndex),
  })),
});

// ─── Premade Layouts ──────────────────────────────────────────────────────────
// These are built-in templates that always appear in the layout dropdown.
// They work the same as user-created layouts but cannot be deleted, renamed,
// or overwritten. Users CAN set them as the default layout.

const PREMADE_LAYOUTS = [
  {
    id: "premade_sales",
    name: "Sales",
    isPremade: true,
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
    activeSort: "None",
    columnFilterModel: {
      loanStatus: {
        filterModels: [
          null,
          {
            filterType: "set",
            values: [
              "Appraisal Ordered",
              "Clear To Close",
              "Condition Clearance",
              "Final Review",
              "Inactive",
              "Processing",
              "Prospect",
              "Terms Accepted",
              "Underwriting",
            ],
          },
        ],
        filterType: "multi",
      },
    },
    columnState: [
      {
        aggFunc: "None",
        colId: "favorite",
        flex: 1,
        hide: true,
        pinned: "left",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 50,
      },
      {
        aggFunc: "None",
        colId: "_id",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanNumber",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 90,
      },
      {
        aggFunc: "None",
        colId: "sync_loan_id",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 150,
      },
      {
        aggFunc: "None",
        colId: "assetId",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "createDateTime",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 115,
      },
      {
        aggFunc: "None",
        colId: "updateDateTime",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 115,
      },
      {
        aggFunc: "None",
        colId: "loanName",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 280,
      },
      {
        aggFunc: "None",
        colId: "comments",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 600,
      },
      {
        aggFunc: "None",
        colId: "loanStatus",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "subStatus",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanCloser",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "underwriter",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "baseLoanToAsIsValue",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "baseLoanToPurchasePrice",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "borrowers",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "processor",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanProductType",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanPurpose",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingScheduled",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingScheduledDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingMonth",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingQuarter",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingWeek",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalArv",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalAsIsValue",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalAssignmentFees",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalConstructionBudget",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalCostBasis",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "totalInterestReserves",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalHoldback",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "baseLoanAmount",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalLoanAmount",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "totalLoanToArv",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalPurchasePrice",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "creditAuthorizations",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "discountPoints",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "dutchInterest",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "existingLoanInDefault",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "fundedDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "interestRate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "isBalanceSheetClosing",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "expectedClosingDate",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "salesperson",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "isTableFunding",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "lastViewDateTime",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanDocuments",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "maturityDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "milestoneStatusChanges",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "netFunding",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "originationDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "originationPoints",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "permittedUsers",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "pipeline",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "referralSource",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "subjectProperties",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "openConditionsCount",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "openExceptionsCount",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalArv_1",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
    ],
    filters: {
      activeView: "all",
      quickFilter: "None",
      statusFilter: "None",
      subStatusFilter: "None",
    },
  },
  // ── Add more premade layouts here as needed ──
  // {
  //   id: "premade_underwriting",
  //   name: "Underwriting",
  //   isPremade: true,
  //   ...
  // },
];

/** Sanitized premade layouts ready for use (all Python "None" → null) */
const SANITIZED_PREMADE_LAYOUTS = PREMADE_LAYOUTS.map(sanitizePremadeLayout);

/** Quick lookup set for premade layout IDs */
const PREMADE_LAYOUT_IDS = new Set(SANITIZED_PREMADE_LAYOUTS.map((l) => l.id));

/** Check if a layout ID belongs to a premade layout */
const isPremadeLayoutId = (id) => PREMADE_LAYOUT_IDS.has(id);

// ─── Storage Helpers ──────────────────────────────────────────────────────────

const LAYOUTS_STORAGE_KEY = "lo-pipeline-layouts";
const DEFAULT_LAYOUT_STORAGE_KEY = "lo-pipeline-default-layout";
const ASSIGNED_TO_ME_STORAGE_KEY = "lo-pipeline-assigned-to-me";
const FAVORITES_STORAGE_KEY = "lo-pipeline-favorite-loans";
const SHOW_FAVORITES_STORAGE_KEY = "lo-pipeline-show-favorites";
const SIDEBAR_COLLAPSED_KEY = "lo-pipeline-sidebar-collapsed";
const DISABLE_AUTO_DEFAULT_LAYOUT_KEY =
  "lo-pipeline-disable-auto-default-layout";

const getSavedLayouts = () => {
  try {
    return safeParseArray(localStorage.getItem(LAYOUTS_STORAGE_KEY));
  } catch {
    return [];
  }
};
const persistLayouts = (l) =>
  localStorage.setItem(
    LAYOUTS_STORAGE_KEY,
    JSON.stringify(Array.isArray(l) ? l : []),
  );

const getDefaultLayoutId = () =>
  safeReadString(localStorage.getItem(DEFAULT_LAYOUT_STORAGE_KEY));

const setDefaultLayoutId = (id) =>
  localStorage.setItem(DEFAULT_LAYOUT_STORAGE_KEY, id ?? "");

const getPersistedAssignedToMe = () => {
  try {
    return safeReadBool(localStorage.getItem(ASSIGNED_TO_ME_STORAGE_KEY));
  } catch {
    return false;
  }
};
const persistAssignedToMe = (v) =>
  localStorage.setItem(ASSIGNED_TO_ME_STORAGE_KEY, v ? "true" : "false");

const getFavoriteLoanIds = () => {
  try {
    return safeParseArray(localStorage.getItem(FAVORITES_STORAGE_KEY));
  } catch {
    return [];
  }
};
const persistFavoriteLoanIds = (ids) =>
  localStorage.setItem(
    FAVORITES_STORAGE_KEY,
    JSON.stringify(Array.isArray(ids) ? ids : []),
  );

const getPersistedShowFavorites = () => {
  try {
    return safeReadBool(localStorage.getItem(SHOW_FAVORITES_STORAGE_KEY));
  } catch {
    return false;
  }
};
const persistShowFavorites = (v) =>
  localStorage.setItem(SHOW_FAVORITES_STORAGE_KEY, v ? "true" : "false");

const getPersistedSidebarCollapsed = () => {
  try {
    return safeReadBool(localStorage.getItem(SIDEBAR_COLLAPSED_KEY));
  } catch {
    return false;
  }
};
const persistSidebarCollapsed = (v) =>
  localStorage.setItem(SIDEBAR_COLLAPSED_KEY, v ? "true" : "false");

const getPersistedDisableAutoDefaultLayout = () => {
  try {
    return safeReadBool(localStorage.getItem(DISABLE_AUTO_DEFAULT_LAYOUT_KEY));
  } catch {
    return false;
  }
};
const persistDisableAutoDefaultLayout = (v) =>
  localStorage.setItem(DISABLE_AUTO_DEFAULT_LAYOUT_KEY, v ? "true" : "false");

// ─── Filter Helpers ───────────────────────────────────────────────────────────

const SUB_FILTER_NONE = "__none__";

const loanBelongsToStatusGroup = (loan, parentStatus) => {
  if (loan?.loanStatus === parentStatus) return true;
  const subs = LOAN_SUBSTATUS_MAP[parentStatus];
  return subs && subs.includes(loan?.loanStatus);
};

const getLoansForStatusGroup = (loans, parentStatus) =>
  loans?.filter((l) => loanBelongsToStatusGroup(l, parentStatus)) || [];

const isSubStatusField = (subStatusValue) =>
  SUBSTATUS_TO_PARENT_MAP[subStatusValue] === "Processing";

const filterLoansBySubStatus = (loans, parentStatus, subStatusFilter) => {
  const groupLoans = getLoansForStatusGroup(loans, parentStatus);
  if (!subStatusFilter) return groupLoans;
  if (subStatusFilter === SUB_FILTER_NONE) {
    return groupLoans.filter(
      (l) =>
        l?.loanStatus === parentStatus &&
        (isNil(l?.subStatus) || l?.subStatus === ""),
    );
  }
  if (isSubStatusField(subStatusFilter)) {
    return groupLoans.filter(
      (l) => l?.loanStatus === parentStatus && l?.subStatus === subStatusFilter,
    );
  }
  return groupLoans.filter(
    (l) =>
      (l?.loanStatus === parentStatus && l?.subStatus === subStatusFilter) ||
      l?.loanStatus === subStatusFilter,
  );
};

const countLoansForSubStatus = (loans, parentStatus, subStatusValue) =>
  size(filterLoansBySubStatus(loans, parentStatus, subStatusValue));

const filterLoansByUserEmail = (loans = [], userEmail) => {
  if (!userEmail) return loans;
  return loans?.filter(
    (l) =>
      l?.salesperson === userEmail ||
      l?.loanCloser === userEmail ||
      l?.processor === userEmail ||
      l?.underwriter === userEmail ||
      l?.permittedUsers?.includes(userEmail),
  );
};

// ─── useLayouts Hook ──────────────────────────────────────────────────────────

const useLayouts = ({ gridRef, activeSort, filters, syncPreferences }) => {
  const [userLayouts, setUserLayouts] = useState(() => getSavedLayouts());
  const [defaultId, setDefaultId] = useState(() => getDefaultLayoutId());

  // Merge premade layouts with user-created layouts
  const layouts = useMemo(
    () => [
      ...SANITIZED_PREMADE_LAYOUTS,
      ...(Array.isArray(userLayouts) ? userLayouts : []),
    ],
    [userLayouts],
  );

  useEffect(() => {
    const onHydrated = () => {
      setUserLayouts(getSavedLayouts());
      setDefaultId(getDefaultLayoutId());
    };
    window.addEventListener("preferences-hydrated", onHydrated);
    return () => window.removeEventListener("preferences-hydrated", onHydrated);
  }, []);

  const saveUserLayouts = useCallback(
    (updated) => {
      // Only persist user layouts — never persist premade layouts to localStorage
      const userOnly = (Array.isArray(updated) ? updated : []).filter(
        (l) => !l.isPremade,
      );
      setUserLayouts(userOnly);
      persistLayouts(userOnly);
      syncPreferences?.();
    },
    [syncPreferences],
  );

  const captureCurrentState = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return null;
    return {
      columnState: api.getColumnState(),
      columnFilterModel: api.getFilterModel(),
      activeSort: activeSort || null,
      filters: {
        statusFilter: filters?.statusFilter ?? null,
        subStatusFilter: filters?.subStatusFilter ?? null,
        activeView: filters?.activeView ?? "all",
        quickFilter: filters?.quickFilter ?? null,
      },
    };
  }, [gridRef, activeSort, filters]);

  const createLayout = useCallback(
    (name) => {
      const state = captureCurrentState();
      if (!state) return;
      const newLayout = {
        id: `layout_${Date.now()}`,
        name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...state,
      };
      const updated = [
        ...(Array.isArray(userLayouts) ? userLayouts : []),
        newLayout,
      ];
      saveUserLayouts(updated);
      return newLayout;
    },
    [userLayouts, captureCurrentState, saveUserLayouts],
  );

  const updateLayout = useCallback(
    (layoutId) => {
      // Premade layouts cannot be overwritten
      if (isPremadeLayoutId(layoutId)) return;
      const state = captureCurrentState();
      if (!state) return;
      const safe = Array.isArray(userLayouts) ? userLayouts : [];
      const updated = safe.map((l) =>
        l.id === layoutId
          ? { ...l, ...state, updatedAt: new Date().toISOString() }
          : l,
      );
      saveUserLayouts(updated);
    },
    [userLayouts, captureCurrentState, saveUserLayouts],
  );

  const renameLayout = useCallback(
    (layoutId, newName) => {
      // Premade layouts cannot be renamed
      if (isPremadeLayoutId(layoutId)) return;
      const safe = Array.isArray(userLayouts) ? userLayouts : [];
      saveUserLayouts(
        safe.map((l) => (l.id === layoutId ? { ...l, name: newName } : l)),
      );
    },
    [userLayouts, saveUserLayouts],
  );

  const deleteLayout = useCallback(
    (layoutId) => {
      // Premade layouts cannot be deleted
      if (isPremadeLayoutId(layoutId)) return;
      const safe = Array.isArray(userLayouts) ? userLayouts : [];
      saveUserLayouts(safe.filter((l) => l.id !== layoutId));
      if (defaultId === layoutId) {
        setDefaultId(null);
        setDefaultLayoutId(null);
        syncPreferences?.();
      }
    },
    [userLayouts, defaultId, saveUserLayouts, syncPreferences],
  );

  const setAsDefault = useCallback(
    (layoutId) => {
      const next = layoutId ?? null;
      setDefaultId(next);
      setDefaultLayoutId(next);
      syncPreferences?.();
    },
    [syncPreferences],
  );

  const toggleDefault = useCallback(
    (layoutId) => {
      const isRemoving = defaultId === layoutId;
      const next = isRemoving ? null : layoutId;
      // Write to localStorage FIRST (synchronous, immediate)
      setDefaultLayoutId(next);
      // Then update React state
      setDefaultId(next);
      // Defer sync so it picks up the new localStorage value
      setTimeout(() => syncPreferences?.(), 50);
    },
    [defaultId, syncPreferences],
  );

  const getDefaultLayout = useCallback(() => {
    if (!defaultId) return null;
    // Search in merged layouts (premade + user)
    return layouts.find((l) => l.id === defaultId) || null;
  }, [layouts, defaultId]);

  return {
    layouts,
    defaultId,
    createLayout,
    updateLayout,
    renameLayout,
    deleteLayout,
    setAsDefault,
    toggleDefault,
    getDefaultLayout,
  };
};

// ─── useFavorites Hook ────────────────────────────────────────────────────────

const useFavorites = (gridRef, syncPreferences) => {
  const [favoriteIds, setFavoriteIds] = useState(() => getFavoriteLoanIds());
  const favoriteIdsRef = useRef(favoriteIds);

  useEffect(() => {
    favoriteIdsRef.current = Array.isArray(favoriteIds) ? favoriteIds : [];
  }, [favoriteIds]);

  useEffect(() => {
    const onHydrated = () => {
      const next = getFavoriteLoanIds();
      setFavoriteIds(next);
      favoriteIdsRef.current = next;
    };
    window.addEventListener("preferences-hydrated", onHydrated);
    return () => window.removeEventListener("preferences-hydrated", onHydrated);
  }, []);

  const toggleFavorite = useCallback(
    (loanId) => {
      setFavoriteIds((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const updated = safePrev.includes(loanId)
          ? safePrev.filter((id) => id !== loanId)
          : [...safePrev, loanId];
        persistFavoriteLoanIds(updated);
        favoriteIdsRef.current = updated;
        return updated;
      });
      syncPreferences?.();
      setTimeout(() => {
        gridRef?.current?.api?.refreshCells({
          columns: ["favorite"],
          force: true,
        });
      }, 0);
    },
    [gridRef, syncPreferences],
  );

  const isFavorite = useCallback((loanId) => {
    const safe = Array.isArray(favoriteIdsRef.current)
      ? favoriteIdsRef.current
      : [];
    return safe.includes(loanId);
  }, []);

  return {
    favoriteIds: Array.isArray(favoriteIds) ? favoriteIds : [],
    toggleFavorite,
    isFavorite,
  };
};

// ─── Favorite Cell Renderer ───────────────────────────────────────────────────

const CellRendererFavorite = (params) => {
  const loanId = params?.data?._id;
  if (!loanId) return null;
  const isFav = params?.context?.isFavorite?.(loanId);
  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        params?.context?.toggleFavorite?.(loanId);
      }}
      sx={{ p: 0.5 }}
    >
      {isFav ? (
        <StarRounded sx={{ fontSize: 20, color: "#f59e0b" }} />
      ) : (
        <StarOutlineRounded sx={{ fontSize: 20, color: "#d1d5db" }} />
      )}
    </IconButton>
  );
};

// ─── Pipeline Nav Item (top-level clickable header) ──────────────────────────

const PipelineNavItem = ({ label, value, currentValue, count, onChange }) => {
  const isSelected = value === currentValue;
  return (
    <ButtonBase
      disableRipple
      onClick={() => onChange?.(value)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        px: 1.5,
        py: "7px",
        borderRadius: "4px",
        bgcolor: isSelected ? "#eff6ff" : "transparent",
        border: isSelected ? "1px solid #1a56db4a" : "none",
        borderRight: isSelected ? "2px solid #1a56db" : "none",
        "&:hover": { bgcolor: isSelected ? "#eff6ff" : "#f0f4f8" },
        transition: "background-color 0.12s",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: isSelected ? 600 : 400,
          color: isSelected ? "#1a56db" : "#374151",
          userSelect: "none",
          textAlign: "left",
          lineHeight: 1.3,
        }}
      >
        {label}

        <Typography
          component={"span"}
          sx={{
            fontSize: 12,
            paddingLeft: 0.5,
            color: isSelected ? "#1d4ed8" : "#94a3b8",
            fontWeight: isSelected ? 600 : 400,
          }}
        >
          ({count})
        </Typography>
      </Typography>
    </ButtonBase>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
// ─── Main Component ───────────────────────────────────────────────────────────
// ─── Main Component ───────────────────────────────────────────────────────────

const LoanOfficerPipelineScreen = ({ children }) => {
  const { syncPreferences } = useUserPreferences();
  const isFetchingRef = useRef(false);
  const gridRef = useRef(null);
  const gridReadyRef = useRef(false);
  const defaultAppliedRef = useRef(false);

  const { loading, getUserPipeline } = usePipelineHook();
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const { userEmail } = useReduxHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const { name: pipelineName } = useParams();

  const [quickFilter, setQuickFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [subStatusFilter, setSubStatusFilter] = useState(null);
  const [activeSort, setActiveSort] = useState(null);
  const [activeLayoutId, setActiveLayoutId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    getPersistedSidebarCollapsed(),
  );
  const [disableAutoDefaultLayout, setDisableAutoDefaultLayout] = useState(() =>
    getPersistedDisableAutoDefaultLayout(),
  );

  const [activeView, setActiveView] = useState(() => {
    if (getPersistedAssignedToMe()) return "assigned";
    if (getPersistedShowFavorites()) return "favorites";
    return "all";
  });

  const [assignedToMe, setAssignedToMe] = useState(() =>
    getPersistedAssignedToMe(),
  );
  const handleAssignedToMeChange = useCallback(
    (checked) => {
      setAssignedToMe(checked);
      persistAssignedToMe(checked);
      syncPreferences();
    },
    [syncPreferences],
  );

  const { favoriteIds, toggleFavorite, isFavorite } = useFavorites(
    gridRef,
    syncPreferences,
  );
  const [showFavorites, setShowFavorites] = useState(() =>
    getPersistedShowFavorites(),
  );
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const handleShowFavoritesChange = useCallback(
    (checked) => {
      setShowFavorites(checked);
      persistShowFavorites(checked);
      syncPreferences();
      if (!checked) setOnlyFavorites(false);
    },
    [syncPreferences],
  );

  const handleOnlyFavoritesChange = useCallback((checked) => {
    setOnlyFavorites(checked);
  }, []);

  const handleDisableAutoDefaultLayoutChange = useCallback(
    (checked) => {
      setDisableAutoDefaultLayout(checked);
      persistDisableAutoDefaultLayout(checked);
      syncPreferences();
    },
    [syncPreferences],
  );

  const handleViewChange = useCallback(
    (val) => {
      const next = val || "all";
      setActiveView(next);
      const isAssigned = next === "assigned";
      const isFavs = next === "favorites";
      setAssignedToMe(isAssigned);
      persistAssignedToMe(isAssigned);
      setOnlyFavorites(isFavs);
      syncPreferences();
    },
    [syncPreferences],
  );

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      persistSidebarCollapsed(!prev);
      return !prev;
    });
  }, []);

  useEffect(() => {
    const onHydrated = () => {
      setAssignedToMe(getPersistedAssignedToMe());
      const nextShowFav = getPersistedShowFavorites();
      setShowFavorites(nextShowFav);
      if (!nextShowFav) setOnlyFavorites(false);
      setDisableAutoDefaultLayout(getPersistedDisableAutoDefaultLayout());
    };
    window.addEventListener("preferences-hydrated", onHydrated);
    return () => window.removeEventListener("preferences-hydrated", onHydrated);
  }, []);

  const layoutAnchorRef = useRef(null);
  const layoutMenuRef = useRef(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState("");
  const [saveDialogMode, setSaveDialogMode] = useState("create");
  const [saveDialogTargetId, setSaveDialogTargetId] = useState(null);

  const PIPELINE_TITLE =
    PIPELINE_NAME_TO_TITLE_MAP[pipelineName] || "Loan Pipeline";

  // ── Memoized filters object for useLayouts ──
  const currentFilters = useMemo(
    () => ({
      statusFilter,
      subStatusFilter,
      activeView,
      quickFilter,
    }),
    [statusFilter, subStatusFilter, activeView, quickFilter],
  );

  const {
    layouts,
    defaultId,
    createLayout,
    updateLayout,
    renameLayout,
    deleteLayout,
    setAsDefault,
    toggleDefault,
    getDefaultLayout,
  } = useLayouts({
    gridRef,
    activeSort,
    filters: currentFilters,
    syncPreferences,
  });

  const applyLayout = useCallback(
    (layout, { skipAutoDefault = false } = {}) => {
      const api = gridRef.current?.api;
      if (!api || !layout) return;

      // Restore column state
      if (layout.columnState)
        api.applyColumnState({ state: layout.columnState, applyOrder: true });

      // Restore AG Grid column filter model
      if (layout.columnFilterModel) {
        api.setFilterModel(layout.columnFilterModel);
      } else {
        api.setFilterModel(null);
      }

      // Restore sort
      if (layout.activeSort) {
        setActiveSort(layout.activeSort);
        api.applyColumnState({
          state: [
            { colId: layout.activeSort.field, sort: layout.activeSort.sort },
          ],
          defaultState: { sort: null },
        });
      } else {
        setActiveSort(null);
        api.applyColumnState({ defaultState: { sort: null } });
      }

      // Restore applied filters (statusFilter, subStatusFilter, activeView, quickFilter)
      if (layout.filters) {
        setStatusFilter(layout.filters.statusFilter ?? null);
        setSubStatusFilter(layout.filters.subStatusFilter ?? null);
        setQuickFilter(layout.filters.quickFilter ?? null);

        // Restore view (all / assigned / favorites) and sync dependent state
        const savedView = layout.filters.activeView ?? "all";
        setActiveView(savedView);
        const isAssigned = savedView === "assigned";
        const isFavs = savedView === "favorites";
        setAssignedToMe(isAssigned);
        persistAssignedToMe(isAssigned);
        setOnlyFavorites(isFavs);
      }

      setActiveLayoutId(layout.id);

      // Auto-set as default unless the setting is disabled or explicitly skipped
      if (!skipAutoDefault && !disableAutoDefaultLayout) {
        setAsDefault(layout.id);
      }
    },
    [disableAutoDefaultLayout, setAsDefault],
  );
  const handleGridReady = useCallback(() => {
    gridReadyRef.current = true;
    if (!defaultAppliedRef.current) {
      // Read directly from localStorage to avoid stale React state
      const persistedDefaultId = getDefaultLayoutId();

      // Look up the default layout from both persisted user layouts AND premade layouts
      const persistedLayouts = getSavedLayouts();
      const allAvailableLayouts = [
        ...SANITIZED_PREMADE_LAYOUTS,
        ...persistedLayouts,
      ];
      const defaultLayout = persistedDefaultId
        ? allAvailableLayouts.find((l) => l.id === persistedDefaultId) || null
        : null;

      if (defaultLayout) {
        setTimeout(() => {
          applyLayout(defaultLayout, { skipAutoDefault: true });
          defaultAppliedRef.current = true;
        }, 100);
      }
    }
  }, [applyLayout]);
  // Pipeline filtering
  const filteredPipelineLoans = useMemo(() => {
    const base = loanPipeline || [];
    if (pipelineName === "all") return base;
    if (pipelineName === "underwriting")
      return base.filter((l) => l?.pipeline === "Underwriting");
    if (pipelineName === "borrower-portal-scenarios")
      return base.filter((l) => l?.pipeline === "Borrower Portal Scenarios");
    if (pipelineName === "scenarios")
      return base.filter((l) => l?.pipeline === "Scenario");
    if (pipelineName === "favorites") {
      const safeFavIds = Array.isArray(favoriteIds) ? favoriteIds : [];
      return base.filter((l) => safeFavIds.includes(l?._id));
    }
    return [];
  }, [loanPipeline, pipelineName, favoriteIds]);
  const filteredPipelineLoansByAssignment = useMemo(() => {
    if (!assignedToMe) return filteredPipelineLoans;
    return filterLoansByUserEmail(filteredPipelineLoans, userEmail);
  }, [filteredPipelineLoans, assignedToMe, userEmail]);

  const filteredPipelineLoansByFavorites = useMemo(() => {
    if (!onlyFavorites) return filteredPipelineLoansByAssignment;
    const safeFavIds = Array.isArray(favoriteIds) ? favoriteIds : [];
    return filteredPipelineLoansByAssignment.filter((l) =>
      safeFavIds.includes(l?._id),
    );
  }, [filteredPipelineLoansByAssignment, onlyFavorites, favoriteIds]);

  const filteredPipelineLoansByStatus = useMemo(() => {
    if (!statusFilter) return filteredPipelineLoansByFavorites;
    const hasSubStatuses = !!LOAN_SUBSTATUS_MAP[statusFilter];
    if (hasSubStatuses)
      return filterLoansBySubStatus(
        filteredPipelineLoansByFavorites,
        statusFilter,
        subStatusFilter,
      );
    return filteredPipelineLoansByFavorites?.filter(
      (l) => l?.loanStatus === statusFilter,
    );
  }, [filteredPipelineLoansByFavorites, statusFilter, subStatusFilter]);

  const getStatusGroupCount = useCallback(
    (parentStatus) => {
      if (!parentStatus) return size(filteredPipelineLoansByFavorites);
      return size(
        getLoansForStatusGroup(filteredPipelineLoansByFavorites, parentStatus),
      );
    },
    [filteredPipelineLoansByFavorites],
  );

  const viewMenuOptions = useMemo(() => {
    const allCount = size(filteredPipelineLoans);
    const assignedCount = size(
      filterLoansByUserEmail(filteredPipelineLoans, userEmail),
    );
    const safeFavIds = Array.isArray(favoriteIds) ? favoriteIds : [];
    const favoritesCount = size(
      filteredPipelineLoans.filter((l) => safeFavIds.includes(l?._id)),
    );

    const counts = {
      all: allCount,
      assigned: assignedCount,
      favorites: favoritesCount,
    };

    return VIEW_OPTIONS.filter(
      (opt) =>
        opt.value !== "favorites" || (showFavorites && !isEmpty(safeFavIds)),
    ).map((opt) => ({ ...opt, count: counts[opt.value] ?? null }));
  }, [filteredPipelineLoans, userEmail, favoriteIds, showFavorites]);

  useEffect(() => {
    setSubStatusFilter(null);
  }, [statusFilter]);

  const activeSortValue = useMemo(
    () => (activeSort ? `${activeSort.field}-${activeSort.sort}` : null),
    [activeSort],
  );

  const handleSortChange = useCallback((val) => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (!val) {
      api.applyColumnState({ defaultState: { sort: null } });
      setActiveSort(null);
      return;
    }
    const sortOption = SORT_OPTIONS.find((o) => `${o.field}-${o.sort}` === val);
    if (!sortOption) return;
    api.applyColumnState({
      state: [{ colId: sortOption.field, sort: sortOption.sort }],
      defaultState: { sort: null },
    });
    setActiveSort(sortOption);
  }, []);

  const settingsOptions = useMemo(
    () => [
      {
        label: "Enable Loan Favoriting (Beta)",
        value: "favorites",
        type: "toggle",
        checked: showFavorites,
        onChange: handleShowFavoritesChange,
      },
      {
        label: "Disable Layout Auto-Default",
        value: "disable-auto-default-layout",
        type: "toggle",
        checked: disableAutoDefaultLayout,
        onChange: handleDisableAutoDefaultLayoutChange,
      },
    ],
    [
      showFavorites,
      handleShowFavoritesChange,
      disableAutoDefaultLayout,
      handleDisableAutoDefaultLayoutChange,
    ],
  );

  const handleSaveNewLayout = useCallback(() => {
    setSaveDialogMode("create");
    setSaveDialogName("");
    setSaveDialogTargetId(null);
    setSaveDialogOpen(true);
    layoutMenuRef.current?.close();
  }, []);

  const handleRenameLayout = useCallback((layout) => {
    setSaveDialogMode("rename");
    setSaveDialogName(layout.name);
    setSaveDialogTargetId(layout.id);
    setSaveDialogOpen(true);
    layoutMenuRef.current?.close();
  }, []);

  const handleSaveDialogConfirm = useCallback(() => {
    const trimmed = saveDialogName.trim();
    if (!trimmed) return;
    if (saveDialogMode === "create") {
      const nl = createLayout(trimmed);
      if (nl) {
        setActiveLayoutId(nl.id);
        // Auto-set newly created layout as default unless disabled
        if (!disableAutoDefaultLayout) {
          setAsDefault(nl.id);
        }
      }
    } else if (saveDialogMode === "rename" && saveDialogTargetId)
      renameLayout(saveDialogTargetId, trimmed);
    setSaveDialogOpen(false);
    setSaveDialogName("");
  }, [
    saveDialogName,
    saveDialogMode,
    saveDialogTargetId,
    createLayout,
    renameLayout,
    disableAutoDefaultLayout,
    setAsDefault,
  ]);

  const handleUpdateLayout = useCallback(
    (layoutId) => {
      updateLayout(layoutId);
      layoutMenuRef.current?.close();
    },
    [updateLayout],
  );

  const handleDeleteLayout = useCallback(
    (layoutId) => {
      deleteLayout(layoutId);
      if (activeLayoutId === layoutId) {
        const defaultLayout = getDefaultLayout();
        if (defaultLayout && defaultLayout.id !== layoutId) {
          applyLayout(defaultLayout, { skipAutoDefault: true });
        } else {
          const api = gridRef.current?.api;
          if (api) {
            api.resetColumnState();
            api.applyColumnState({ defaultState: { sort: null } });
            api.setFilterModel(null);
          }
          setActiveSort(null);
          setActiveLayoutId(null);
          setStatusFilter(null);
          setSubStatusFilter(null);
          setQuickFilter(null);
          setActiveView("all");
          setAssignedToMe(false);
          persistAssignedToMe(false);
          setOnlyFavorites(false);
        }
      }
    },
    [deleteLayout, activeLayoutId, getDefaultLayout, applyLayout],
  );

  const handleApplyLayout = useCallback(
    (layout) => {
      applyLayout(layout);
      layoutMenuRef.current?.close();
    },
    [applyLayout],
  );
  const handleClearActiveLayout = useCallback(() => {
    const api = gridRef.current?.api;
    if (api) {
      api.resetColumnState();
      api.applyColumnState({ defaultState: { sort: null } });
      api.setFilterModel(null);
    }
    setActiveSort(null);
    setActiveLayoutId(null);
    setStatusFilter(null);
    setSubStatusFilter(null);
    setQuickFilter(null);
    setActiveView("all");
    setAssignedToMe(false);
    persistAssignedToMe(false);
    setOnlyFavorites(false);

    // Clear the default layout so nothing auto-applies on next load
    setAsDefault(null);
    layoutMenuRef.current?.close();
  }, [setAsDefault]);

  const fetchLoanPipeline = useCallback(() => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    getUserPipeline({
      onSuccessFn: (data) => {
        dispatch(loanPipelineSet(data));
        isFetchingRef.current = false;
      },
      onErrorFn: (err) => {
        console.error("GetUserPipeline error:", err);
        isFetchingRef.current = false;
      },
    });
  }, [getUserPipeline, dispatch]);

  const onCellClicked = useCallback(
    (params) => {
      if (
        params?.colDef?.field === "loanNumber" ||
        params?.colDef?.field === "favorite"
      )
        return;
      const el = params.event?.target;
      if (
        (el instanceof Element && el.closest("button")) ||
        (el instanceof Element && el.closest("a"))
      )
        return;
      if (isNil(params?.data?.loanNumber)) {
        console.warn("No loanNumber found");
        return;
      }
      if (params?.colDef?.field === "loanName")
        navigate(`/loan/${params.data.loanNumber}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (isNil(loanPipeline) && !isFetchingRef.current) fetchLoanPipeline();
    if (!isNil(loanDrilldown)) clearLoanDrilldownData();
  }, [loanPipeline, fetchLoanPipeline, loanDrilldown]);

  useEffect(() => {
    if (!pipelineName || !PIPELINE_NAME_TO_TITLE_MAP[pipelineName])
      navigate("/loans/all", { replace: true });
  }, [pipelineName, navigate]);

  const activeLayoutName = useMemo(() => {
    if (!activeLayoutId) return null;
    const safe = Array.isArray(layouts) ? layouts : [];
    return safe.find((l) => l.id === activeLayoutId)?.name || null;
  }, [activeLayoutId, layouts]);

  const handlePipelineChange = useCallback(
    (v) => navigate(`/loans/${v}`),
    [navigate],
  );

  // Split layouts into premade vs user for rendering with a separator
  const premadeLayouts = useMemo(
    () => layouts.filter((l) => l.isPremade),
    [layouts],
  );
  const userCreatedLayouts = useMemo(
    () => layouts.filter((l) => !l.isPremade),
    [layouts],
  );

  return (
    <Box
      sx={{
        background: "#ffffff",
        display: "flex",
        overflow: "hidden",
        height: "100%",
      }}
    >
      {/* ── Main Content ── */}
      <ScreenContent
        sx={{
          height: "100%",
          p: 0,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            background: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              p: 1,
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              flexGrow: isMinimized ? 0 : 1,
              width: "100%",
            }}
          >
            {isMinimized ? (
              <Flx
                g={2}
                ac
                sx={{
                  flexGrow: 0,
                  border: "1px solid #e0e0e0",
                  background: grey[50],
                  p: 1,
                  borderRadius: "8px",
                }}
              >
                <Flx ac>
                  <Flx ac>
                    <ForkRightOutlined
                      className="thin"
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
                    />

                    <Txt secondary ml={0.5} sx={{ fontSize: 12 }}>
                      Pipeline:{" "}
                      <b
                        style={{
                          textTransform: "capitalize",
                          color: "#1a56db",
                        }}
                      >
                        {pipelineName || "All"}
                      </b>
                    </Txt>
                  </Flx>
                </Flx>
                <EastOutlined className="thin" sx={{ fontSize: "15px" }} />

                <Txt secondary sx={{ fontSize: 12 }}>
                  Loan Status:{" "}
                  <b
                    style={{
                      textTransform: "capitalize",
                      color: "#1a56db",
                    }}
                  >
                    {statusFilter || "All"}
                  </b>
                </Txt>
                {!isNil(subStatusFilter) && subStatusFilter !== "__none__" && (
                  <>
                    <EastOutlined className="thin" sx={{ fontSize: "15px" }} />

                    <Txt secondary sx={{ fontSize: 12 }}>
                      Sub Status:{" "}
                      <b
                        style={{
                          textTransform: "capitalize",
                          color: "#1a56db",
                        }}
                      >
                        {isNil(subStatusFilter) ||
                          subStatusFilter === "__none__" ||
                          "All"}
                      </b>
                    </Txt>
                  </>
                )}
                <Tooltip onClick={() => setIsMinimized(false)} title="Expand">
                  <IconButton sx={{ ml: 2.5 }} size="small">
                    <ExpandMoreOutlined />
                  </IconButton>
                </Tooltip>
              </Flx>
            ) : (
              <Flx
                column
                sx={{
                  flexGrow: 0,
                  width: "max-content",
                  border: "1px solid #e0e0e0",
                  background: grey[50],
                  borderRadius: "8px",
                }}
              >
                <Flx
                  ac
                  jb
                  g={1}
                  sx={{
                    p: 1,
                    flexGrow: 0,
                    px: 1.2,
                    py: 0.6,
                  }}
                >
                  <PipelineSwitcher
                    pipelineName={pipelineName}
                    loanPipeline={loanPipeline}
                    onPipelineChange={handlePipelineChange}
                    assignedToMe={assignedToMe}
                    userEmail={userEmail}
                    favoriteIds={favoriteIds}
                  />

                  <Tooltip
                    onClick={() => setIsMinimized(true)}
                    title="Collapse"
                  >
                    <IconButton size="small">
                      <ExpandLessOutlined />
                    </IconButton>
                  </Tooltip>
                </Flx>
                <Divider sx={{ borderColor: "#e0e0e0" }} />
                <Flx
                  g={1}
                  sx={{ p: 1, px: 1.2, alignItems: "flex-start", flexGrow: 1 }}
                >
                  <PipelineStatusFilterSwitcher
                    loans={filteredPipelineLoansByFavorites}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    getStatusGroupCount={getStatusGroupCount}
                    sx={{
                      borderBottomLeftRadius: isNil(
                        LOAN_SUBSTATUS_MAP?.[statusFilter],
                      )
                        ? "8px"
                        : 0,
                    }}
                    subStatusContent={
                      <SubStatusSwitcher
                        loans={filteredPipelineLoansByAssignment}
                        statusFilter={statusFilter}
                        subStatusFilter={subStatusFilter}
                        setSubStatusFilter={setSubStatusFilter}
                        sx={{
                          background: "#eceff196",
                          border: "1px solid #e0e0e0",
                          borderTop: "none",
                          borderRadius: "8px",
                          borderTopRightRadius: 0,
                          borderTopLeftRadius: 0,
                          px: 1,
                          width: "max-content",
                        }}
                      />
                    }
                  />
                </Flx>
              </Flx>
            )}

            <Flx
              wrap
              end
              sx={{
                flexGrow: 0,
                alignItems: "flex-start",
                mt: 1,
                flexBasis: "fit-content",
                height: "max-content",
              }}
              g={1}
            >
              <DownloadDropdownButton />
              <CreateLoanButton size="medium" />
            </Flx>
          </Box>

          <Flx
            ac
            jb
            wrap
            g={2}
            sx={{
              p: 1,
              pt: 1,
              borderTop: "1px solid #e0e0e0",
              flexGrow: 1,
              px: 1.2,
            }}
          >
            <Flx ac g={1} wrap>
              <SearchInput
                sx={{
                  width: "225px",
                  ".MuiInputBase-root": {
                    borderRadius: "50px !important",
                    background: "#ffffff",
                    fontSize: "11px",
                    pl: 1.2,
                    pr: 1,
                    ".MuiSvgIcon-root": { fontSize: "11px" },
                  },
                  input: { height: "unset", fontSize: "11px" },
                  fieldset: { border: "none" },
                }}
                value={quickFilter}
                onChange={setQuickFilter}
              />
              <MyLoansCheckbox
                activeView={activeView}
                onViewChange={handleViewChange}
                assignedCount={
                  viewMenuOptions.find((o) => o.value === "assigned")?.count ??
                  0
                }
              />
            </Flx>
            <Flx ac g={2}>
              <ButtonMenu
                menuTitle="Sort Options"
                options={SORT_MENU_OPTIONS}
                value={activeSortValue}
                startIcon={
                  <SwapVertOutlined
                    sx={{ fontSize: "16px !important", ml: 0.5 }}
                  />
                }
                onChange={handleSortChange}
                buttonText={activeSortValue ? null : "Sort: None"}
                allowDeselect
                showBadge={activeSortValue !== null}
                menuMinWidth={280}
              />
              <Badge color="primary" variant="dot" invisible={!activeLayoutId}>
                <Button
                  startIcon={
                    <ViewComfyOutlined
                      className="thin"
                      sx={{ fontSize: "16px !important" }}
                    />
                  }
                  ref={layoutAnchorRef}
                  sx={{
                    border: "1px solid #e0e0e0",
                    fontWeight: activeLayoutId ? 600 : 400,
                    textTransform: "none",
                    pb: "3px",
                    borderRadius: "8px",
                  }}
                  size="medium"
                  color={activeLayoutId ? "primary" : "gray"}
                  endIcon={<KeyboardArrowDown />}
                  onClick={() => layoutMenuRef.current?.toggle()}
                  variant="outlined"
                >
                  {activeLayoutName
                    ? `Layout: ${activeLayoutName}`
                    : null || "Default Layout"}
                </Button>
              </Badge>
              <StyledMenu
                ref={layoutMenuRef}
                anchorRef={layoutAnchorRef}
                title="Table Layouts"
                description="Create and manage your preferred table layouts."
                minWidth={300}
                maxWidth={
                  isEmpty(premadeLayouts) && isEmpty(userCreatedLayouts)
                    ? 180
                    : 420
                }
              >
                <Flx ac jb g={1} sx={{ p: 1.5 }}>
                  <Button
                    size="large"
                    variant="outlined"
                    color="primary"
                    sx={{ flexGrow: 1 }}
                    startIcon={<AddRounded fontSize="small" />}
                    onClick={handleSaveNewLayout}
                  >
                    Save Current Table As Layout
                  </Button>
                </Flx>
                <Divider />

                {/* ── Premade Templates Section ── */}
                {size(premadeLayouts) > 0 && (
                  <Box sx={{ py: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        px: 2,
                        pt: 0.5,
                        pb: 0.5,
                      }}
                    >
                      Templates
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      {premadeLayouts.map((layout) => {
                        const isActive = activeLayoutId === layout.id;
                        const isDefaultLayout = defaultId === layout.id;
                        return (
                          <MenuItem
                            key={layout.id}
                            sx={{
                              py: 0.75,
                              px: 1,
                              mx: 0.75,
                              borderRadius: "8px",
                              "&:hover .layout-actions": { opacity: 1 },
                            }}
                            onClick={() => handleApplyLayout(layout)}
                            selected={isActive}
                          >
                            <ListItemText
                              primary={
                                <Flx ac g={0.75} sx={{ mr: 2 }}>
                                  <Radio
                                    checked={isActive}
                                    size="small"
                                    sx={{
                                      p: 0,
                                      color: "#d1d5db",
                                      "&.Mui-checked": { color: "#2563eb" },
                                    }}
                                    disableRipple
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: isActive ? 600 : 400,
                                    }}
                                  >
                                    {layout.name}
                                  </Typography>
                                  <Chip
                                    label="Template"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      color: "#6b7280",
                                      borderColor: "#d1d5db",
                                      "& .MuiChip-label": {
                                        px: 0.75,
                                        fontSize: "10px",
                                        fontStyle: "italic",
                                      },
                                    }}
                                  />
                                  {isDefaultLayout && (
                                    <Chip
                                      label="Default"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{
                                        height: 20,
                                        "& .MuiChip-label": {
                                          px: 0.75,
                                          fontStyle: "italic",
                                          fontSize: "11px",
                                        },
                                      }}
                                    />
                                  )}
                                </Flx>
                              }
                            />
                            <Flx
                              ac
                              className="layout-actions"
                              sx={{
                                opacity: isActive || isDefaultLayout ? 1 : 0,
                                transition: "opacity 0.15s",
                                ml: 1,
                              }}
                            >
                              <Tooltip
                                title={
                                  isDefaultLayout
                                    ? "Remove as default"
                                    : "Set as default"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDefault(layout.id);
                                  }}
                                  sx={{ p: 0.5 }}
                                >
                                  {isDefaultLayout ? (
                                    <StarRounded
                                      sx={{ fontSize: 18, color: "#f59e0b" }}
                                    />
                                  ) : (
                                    <StarOutlineRounded
                                      sx={{ fontSize: 18, color: "#9ca3af" }}
                                    />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {/* No overwrite, rename, or delete for premade layouts */}
                            </Flx>
                          </MenuItem>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* ── Separator between premade and user layouts ── */}
                {size(premadeLayouts) > 0 && size(userCreatedLayouts) > 0 && (
                  <Divider />
                )}

                {/* ── User-Created Layouts Section ── */}
                {size(userCreatedLayouts) === 0 &&
                size(premadeLayouts) === 0 ? (
                  <Box sx={{ px: 2, py: 2, pt: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: 12, fontStyle: "italic" }}
                    >
                      No layouts created yet.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                    }}
                  >
                    {size(userCreatedLayouts) > 0 && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          px: 2,
                          pt: 0.5,
                          pb: 0.5,
                        }}
                      >
                        My Layouts
                      </Typography>
                    )}
                    {userCreatedLayouts.map((layout) => {
                      const isActive = activeLayoutId === layout.id;
                      const isDefaultLayout = defaultId === layout.id;
                      const hasFilters =
                        layout.filters &&
                        (layout.filters.statusFilter ||
                          layout.filters.subStatusFilter ||
                          layout.filters.quickFilter ||
                          layout.filters.activeView !== "all");
                      return (
                        <MenuItem
                          key={layout.id}
                          sx={{
                            py: 0.75,
                            px: 1,
                            mx: 0.75,
                            borderRadius: "8px",
                            "&:hover .layout-actions": { opacity: 1 },
                          }}
                          onClick={() => handleApplyLayout(layout)}
                          selected={isActive}
                        >
                          <ListItemText
                            primary={
                              <Flx ac g={0.75} sx={{ mr: 2 }}>
                                <Radio
                                  checked={isActive}
                                  size="small"
                                  sx={{
                                    p: 0,
                                    color: "#d1d5db",
                                    "&.Mui-checked": { color: "#2563eb" },
                                  }}
                                  disableRipple
                                />
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: isActive ? 600 : 400,
                                  }}
                                >
                                  {layout.name}
                                </Typography>
                                {isDefaultLayout && (
                                  <Chip
                                    label="Default"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      "& .MuiChip-label": {
                                        px: 0.75,
                                        fontStyle: "italic",
                                        fontSize: "11px",
                                      },
                                    }}
                                  />
                                )}
                                {hasFilters && (
                                  <Chip
                                    icon={
                                      <FilterAltOutlined
                                        sx={{ fontSize: "12px !important" }}
                                      />
                                    }
                                    label="Filters"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      color: "#6b7280",
                                      borderColor: "#d1d5db",
                                      "& .MuiChip-label": {
                                        px: 0.5,
                                        fontSize: "10px",
                                      },
                                      "& .MuiChip-icon": {
                                        ml: 0.5,
                                      },
                                    }}
                                  />
                                )}
                              </Flx>
                            }
                          />
                          <Flx
                            ac
                            className="layout-actions"
                            sx={{
                              opacity: isActive || isDefaultLayout ? 1 : 0,
                              transition: "opacity 0.15s",
                              ml: 1,
                            }}
                          >
                            <Tooltip
                              title={
                                isDefaultLayout
                                  ? "Remove as default"
                                  : "Set as default"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDefault(layout.id);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                {isDefaultLayout ? (
                                  <StarRounded
                                    sx={{ fontSize: 18, color: "#f59e0b" }}
                                  />
                                ) : (
                                  <StarOutlineRounded
                                    sx={{ fontSize: 18, color: "#9ca3af" }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Overwrite with current table configuration">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateLayout(layout.id);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <SaveOutlined
                                  sx={{ fontSize: 16, color: "#6b7280" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rename">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRenameLayout(layout);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <EditRounded
                                  sx={{ fontSize: 16, color: "#6b7280" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete layout">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteLayout(layout.id);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteOutlineRounded
                                  sx={{ fontSize: 16, color: "#ef4444" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Flx>
                        </MenuItem>
                      );
                    })}
                    {activeLayoutName && (
                      <>
                        <Divider sx={{ mt: 1 }} />
                        <MenuItem
                          sx={{
                            py: 0.75,
                            px: 1,
                            mx: 0.75,
                            borderRadius: "8px",
                          }}
                          onClick={handleClearActiveLayout}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{ fontSize: "12px", fontWeight: 400 }}
                              >
                                Reset to default
                              </Typography>
                            }
                          />
                        </MenuItem>
                      </>
                    )}
                  </Box>
                )}
              </StyledMenu>
              <ButtonMenu
                options={settingsOptions}
                onChange={(val) => {
                  const opt = settingsOptions.find((o) => o.value === val);
                  if (opt?.type === "toggle" && opt?.onChange) {
                    opt.onChange(!opt.checked);
                  }
                }}
                buttonText="Settings"
                buttonSx={{ border: "1px solid #e0e0e0" }}
                startIcon={
                  <SettingsOutlined
                    sx={{ fontSize: "16px !important", ml: 0.5 }}
                  />
                }
                color="gray"
                menuTitle="Table Settings"
                menuMinWidth={280}
              />
            </Flx>
          </Flx>
        </Box>

        {/* AG Grid Table */}
        <LoanOfficerPipelineTable
          gridRef={gridRef}
          loading={loading}
          onCellClicked={onCellClicked}
          quickFilter={quickFilter}
          rowData={filteredPipelineLoansByStatus}
          onGridReady={handleGridReady}
          showFavorites={showFavorites}
          toggleFavorite={toggleFavorite}
          isFavorite={isFavorite}
        />
      </ScreenContent>

      {/* Save / Rename Dialog */}
      <BasicModal
        title={
          saveDialogMode === "create"
            ? "Create New Layout"
            : "Rename Existing Layout"
        }
        description={
          saveDialogMode === "create"
            ? "Save your current layout configuration as a new layout."
            : null
        }
        show={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <>
          <InputWrapper label="Layout Name">
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={saveDialogName}
              onChange={(e) => setSaveDialogName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveDialogConfirm();
              }}
            />
          </InputWrapper>
          <Flx end g={1} sx={{ mt: 2 }}>
            <Button
              size="large"
              onClick={() => setSaveDialogOpen(false)}
              color="inherit"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              size="large"
              onClick={handleSaveDialogConfirm}
              variant="contained"
              disabled={!saveDialogName.trim()}
            >
              {saveDialogMode === "create" ? "Save" : "Rename"}
            </Button>
          </Flx>
        </>
      </BasicModal>
    </Box>
  );
};

const PipelineSwitcher = ({
  pipelineName,
  loanPipeline,
  onPipelineChange,
  assignedToMe,
  userEmail,
  favoriteIds,
  fontSize = 15,
  showFavorites,
}) => {
  const effectiveLoans = useMemo(() => {
    if (!assignedToMe) return loanPipeline || [];
    return filterLoansByUserEmail(loanPipeline || [], userEmail);
  }, [loanPipeline, assignedToMe, userEmail]);

  const safeFavIds = useMemo(
    () => (Array.isArray(favoriteIds) ? favoriteIds : []),
    [favoriteIds],
  );

  const pipelineTabs = useMemo(
    () => [
      {
        label: "All",
        value: "all",
        count: size(effectiveLoans),
      },
      {
        label: "Underwriting",
        value: "underwriting",
        count: size(
          effectiveLoans?.filter((l) => l?.pipeline === "Underwriting"),
        ),
      },
      {
        label: "Scenarios",
        value: "scenarios",
        count: size(effectiveLoans?.filter((l) => l?.pipeline === "Scenario")),
      },
      {
        label: "Borrower Portal",
        value: "borrower-portal-scenarios",
        count: size(
          effectiveLoans?.filter(
            (l) => l?.pipeline === "Borrower Portal Scenarios",
          ),
        ),
      },
      ...(!isEmpty(safeFavIds)
        ? [
            {
              label: "Favorites",
              value: "favorites",
              count: size(
                effectiveLoans?.filter((l) => safeFavIds.includes(l?._id)),
              ),
            },
          ]
        : []),
    ],
    [effectiveLoans, safeFavIds],
  );

  return (
    <Flx
      wrap
      g={1}
      sx={{
        flexGrow: 1,
      }}
    >
      <Flx ac sx={{ mt: 1.3, height: "max-content", p: 0.1 }}>
        <ForkRightOutlined
          className="thin"
          sx={{ fontSize: fontSize + 2, color: "rgba(0, 0, 0, 0.6)" }}
        />

        <Txt
          secondary
          ml={0.5}
          sx={{ fontSize: fontSize, fontWeight: 500, letterSpacing: 0.5 }}
        >
          Pipeline:
        </Txt>
      </Flx>
      <Switcher
        value={pipelineName}
        onChange={onPipelineChange}
        tabs={pipelineTabs}
        fontSize={fontSize}
        sx={{ p: 0.35 }}
        fontWeight={300}
        activeFontCountWeight={400}
        fontCountWeight={400}
        activeFontWeight={600}
        buttonPadding={{ px: 2, py: 0.85 }}
        activeBorderBottomDisable
      />
    </Flx>
  );
};

const PipelineStatusFilterSwitcher = ({
  statusFilter,
  setStatusFilter,
  getStatusGroupCount,
  loans,
  subStatusContent,
  fontSize = 12,
  sx,
}) => {
  const statusTabs = useMemo(
    () => [
      { label: "All", value: null, count: size(loans) },
      ...LOAN_STATUS_OPTIONS.map((status) => ({
        label: status,
        value: status,
        count: getStatusGroupCount(status),
      })),
    ],
    [loans, getStatusGroupCount],
  );

  return (
    <Flx
      wrap
      sx={{
        flexGrow: 1,
      }}
    >
      <Flx ac sx={{ mt: 1.3, height: "max-content", p: 0.1, mr: 1.2 }}>
        <FilterAltOutlined
          className="thin"
          sx={{ fontSize: 15, color: "rgba(0, 0, 0, 0.6)" }}
        />
        <Txt
          secondary
          sx={{ fontSize: fontSize, ml: 0.5, flexShrink: 0, fontWeight: 400 }}
        >
          Loan Status:
        </Txt>
      </Flx>
      <Flx column>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            background: "#eceff196",
            borderRadius: "8px",
            p: 0.35,
            ...sx,
          }}
        >
          <Switcher
            activeColor="#2962ff"
            value={statusFilter}
            fontSize={fontSize}
            fontWeight={400}
            activeFontWeight={500}
            activeBorderBottomDisable
            buttonPadding={{ px: 1, py: 0.75 }}
            onChange={setStatusFilter}
            tabs={statusTabs}
            sx={sx}
          />
        </Box>
        {subStatusContent}
      </Flx>
    </Flx>
  );
};

// ─── Generic Sub-Status Switcher ─────────────────────────────────────────────

const SubStatusSwitcher = ({
  loans,
  statusFilter,
  subStatusFilter,
  setSubStatusFilter,
  sx,
  fontSize = 11.5,
}) => {
  const subStatuses = LOAN_SUBSTATUS_MAP[statusFilter];
  if (!statusFilter || !subStatuses) return null;

  const noSubStatusCount = countLoansForSubStatus(
    loans,
    statusFilter,
    SUB_FILTER_NONE,
  );
  const allCount = countLoansForSubStatus(loans, statusFilter, null);

  const subTabs = [
    { label: `All`, value: null, count: allCount },
    {
      label: `${statusFilter} (Unassigned)`,
      value: SUB_FILTER_NONE,
      count: noSubStatusCount,
    },
    ...subStatuses.map((sub) => ({
      label: sub,
      value: sub,
      count: countLoansForSubStatus(loans, statusFilter, sub),
    })),
  ];

  return (
    <Flx ac sx={sx}>
      <SubdirectoryArrowRight sx={{ fontSize: 12 }} />
      <Txt
        secondary
        sx={{ mr: 1, flexShrink: 0, fontStyle: "italic", fontSize }}
        ml={0.5}
        mb={0}
      >
        {statusFilter} Sub-Status:
      </Txt>
      <Switcher
        fontSize={fontSize}
        buttonPadding={{ px: 1, py: 0.75 }}
        value={subStatusFilter}
        activeBorderRadius={0}
        activeFontWeight={500}
        activeBackgroundColor={"transparent"}
        activeBorderColor="transparent"
        onChange={setSubStatusFilter}
        tabs={subTabs}
      />
    </Flx>
  );
};

const Switcher = ({
  tabs,
  onChange,
  value,
  sx,
  column = false,
  buttonPadding = { px: 1.75, py: 0.75 },
  fontSize = 14,
  backgroundColor,
  activeBackgroundColor = "#eff6ff",
  activeColor = "#2962ff",
  activeFontWeight = 700,
  activeBorderColor = "#1a56db4a",
  activeBorderRadius = "4px",
  activeBorderBottomDisable = false,
  color,
  ...rest
}) => {
  return (
    <FilterTabSwitcher
      fontSize={fontSize}
      value={value}
      activeBackgroundColor={activeBackgroundColor}
      buttonPadding={buttonPadding}
      inactiveCountColor={grey[600]}
      backgroundColor={"transparent"}
      activeColor={activeColor || "#1a56db"}
      fontWeight={400}
      color={color || "#181d1f"}
      activeFontWeight={activeFontWeight}
      sx={{
        flexWrap: "wrap",
        flexGrow: 1,
        gap: 0,
        p: 0,
        ".selected": {
          boxShadow: "none",
          border: `1px solid ${activeBorderColor}`,
          borderBottom: activeBorderBottomDisable
            ? `1px solid ${activeBorderColor}`
            : "2px solid #1a56db",
          borderRadius: activeBorderRadius,
        },
        ...sx,
      }}
      onChange={onChange}
      tabs={tabs}
      {...rest}
    />
  );
};
// ─── AG Grid Table ────────────────────────────────────────────────────────────

const LoanOfficerPipelineTable = ({
  gridRef,
  rowData,
  onRowClicked,
  quickFilter,
  loading,
  onCellClicked,
  onGridReady,
  showFavorites,
  toggleFavorite,
  isFavorite,
}) => {
  const [showGrandTotal, setShowGrandTotal] = useState(false);

  const onColumnRowGroupChanged = useCallback((params) => {
    const hasGroups = params.api.getRowGroupColumns().length > 0;
    setShowGrandTotal(hasGroups);
  }, []);
  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "favorite",
        hide: !showFavorites,
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        suppressColumnsToolPanel: true,
        sortable: false,
        resizable: false,
        filter: false,
        cellRenderer: CellRendererFavorite,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        },
        pinned: "left",
        lockPosition: true,
      },
      { hide: true, headerName: "_id", field: "_id" },
      {
        headerName: "Loan #",
        field: "loanNumber",
        minWidth: 90,
        maxWidth: 90,
        width: 90,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        flex: 0,
        getQuickFilterText: (params) =>
          params.value ? String(params.value) : "",
        filter: "agTextColumnFilter",
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return (
            <OpenInNewTabButton
              linkIcon
              to={`/loan/${params?.data?.loanNumber}`}
            >
              {params?.data?.loanNumber}
            </OpenInNewTabButton>
          );
        },
        suppressFloatingFilterButton: true,
      },
      {
        field: "sync_loan_id",
        headerName: "External Loan ID",
        hide: true,
        width: 150,
        minWidth: 150,
        flex: 0,
      },
      { headerName: "Asset ID", field: "assetId", hide: true },
      {
        headerName: "Created",
        field: "createDateTime",
        type: "date",
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        wrapHeaderText: true,
        cellRenderer: (p) => {
          if (p.node.group) return null;
          return (
            <CellRendererStackedChip
              icon={<HourglassEmptyOutlined className="thin-8" />}
              params={p}
            />
          );
        },
      },
      {
        headerName: "Updated",
        field: "updateDateTime",
        type: "date",
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        wrapHeaderText: true,
        suppressHeaderFilterButton: true,
        cellRenderer: (p) => {
          if (p.node.group) return null;
          return (
            <CellRendererStackedChip
              icon={<UpdateRounded className="thin-8" />}
              params={p}
            />
          );
        },
      },
      {
        headerName: "Loan Name",
        field: "loanName",
        minWidth: 280,
        width: 360,
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return <CellRendererLoanName {...params} />;
        },
        suppressSizeToFit: true,
        valueGetter: (params) =>
          isNil(params?.data?.loanName)
            ? `Loan #${params?.data?.loanNumber}`
            : params?.data?.loanName,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        autoHeight: true,
        suppressFloatingFilterButton: true,
        headerTooltip: "Go to loan",
        cellStyle: { cursor: "pointer" },
      },
      {
        headerName: "Comments",
        field: "comments",
        suppressAutoSize: true,
        suppressSizeToFit: true,
        suppressHeaderFilterButton: true,
        width: 600,
        minWidth: 350,
        flex: 0,
        autoHeight: true,
        wrapText: true,
        suppressFloatingFilterButton: true,
        cellStyle: {
          paddingTop: "14px",
          paddingBottom: "14px",
          paddingLeft: 0,
          cursor: "pointer",
        },
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return <CellRendererLoanComments {...params} />;
        },

        cellClass: (params) => {
          if (isEmpty(params?.value)) return "ag-cell-vertical-center";
        },
        comparator: (valueA, valueB) => {
          const hasA = valueA && Array.isArray(valueA) && valueA.length > 0;
          const hasB = valueB && Array.isArray(valueB) && valueB.length > 0;
          if (!hasA && !hasB) return 0;
          if (!hasA) return -1;
          if (!hasB) return 1;
          return (
            new Date(valueA[0].commentDateTime).getTime() -
            new Date(valueB[0].commentDateTime).getTime()
          );
        },
      },
      {
        headerName: "Expected Closing Date",
        field: "expectedClosingDate",
        type: "date",
        flex: 0,
        width: 180,
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return <CellRendererEditableLoanField {...params} />;
        },
        cellStyle: (p) => {
          const valueDate = p?.value ? new Date(p.value) : null;
          const isPastDate = valueDate && isBefore(valueDate, new Date());
          const closedStatuses = [
            "Closed",
            "Closed w/ Post Closing Conditions",
            "Balance Sheet",
            "Loan Sold",
            "Inactive",
            "Dead",
          ];
          const isClosedLoan = closedStatuses.includes(p?.data?.loanStatus);
          if (isPastDate)
            return {
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              background: isClosedLoan ? "#e8f5e9ab" : "#ffebee6b",
            };
          return { cursor: "pointer", display: "flex", alignItems: "center" };
        },
      },
      {
        headerName: "Closing Cutoff Date",
        field: "closingCutoffDate",
        type: "date",
        valueFormatter: (params) => {
          if (isNil(params.value)) return "Not Set";
          return params.value;
        },
        cellRenderer: (params) => {
          if (params.node.group) return null;

          return <CellRendererEditableLoanField {...params} />;
        },
      },
      {
        headerName: "Loan Product Type",
        field: "loanProductType",
        flex: 0,
        width: 200,
      },
      { headerName: "Loan Purpose", field: "loanPurpose", flex: 0, width: 200 },
      {
        headerName: "Loan Status",
        field: "loanStatus",
        minWidth: 180,
        cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
        flex: 0,
      },
      {
        headerName: "Sub Status",
        field: "subStatus",
        flex: 0,
        width: 200,
        minWidth: 150,
        cellStyle: { display: "flex", alignItems: "center" },
      },

      {
        headerName: "Salesperson",
        field: "salesperson",
        cellRenderer: (p) => {
          return <CellRendererUserByEmail {...p} stacked={true} />;
        },
      },
      { headerName: "Loan Closer", field: "loanCloser", hide: true },
      { headerName: "Processor", field: "processor", hide: true },
      { headerName: "Underwriter", field: "underwriter", hide: true },
      // Hidden columns

      { field: "baseLoanToAsIsValue", type: "percent", hide: true },
      { field: "baseLoanToPurchasePrice", type: "percent", hide: true },
      {
        headerName: "Base Loan Amount",
        field: "baseLoanAmount",
        type: "dollar",
        cellRenderer: CellRendererEditableLoanField,
        hide: true,
      },
      {
        headerName: "Borrowers",
        field: "borrowers",
        hide: true,
        valueGetter: (params) => {
          const v = params.data?.borrowers;
          if (isEmpty(v) || isNil(v)) return;
          return v.map((item) =>
            isEmpty(item) || isNil(item?.firstName) || isNil(item?.lastName)
              ? "N/A"
              : `${item.firstName} ${item.lastName}`,
          );
        },
      },
      {
        headerName: "Closing Scheduled",
        field: "closingScheduled",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Closing Scheduled Date",
        field: "closingScheduledDate",
        type: "boolean",
        hide: true,
      },
      { field: "closingMonth", hide: true },
      { field: "closingQuarter", hide: true },
      { field: "closingWeek", hide: true },
      { field: "openConditionsCount", hide: true },
      { field: "openExceptionsCount", hide: true },

      { field: "totalArv", type: "dollar", hide: true },
      { field: "totalArv", type: "dollar", hide: true },
      { field: "totalAsIsValue", type: "dollar", hide: true },
      { field: "totalAssignmentFees", type: "dollar", hide: true },
      { field: "totalConstructionBudget", type: "dollar", hide: true },
      { field: "totalCostBasis", type: "dollar", hide: true },
      { field: "totalInterestReserves", type: "percent", hide: true },
      {
        headerName: "Total Holdback",
        field: "totalHoldback",
        type: "dollar",
        hide: true,
      },
      {
        headerName: "Total Loan Amount",
        field: "totalLoanAmount",
        type: "dollar",
        hide: true,
      },
      { field: "totalLoanToArv", type: "percent", hide: true },
      { field: "totalPurchasePrice", type: "dollar", hide: true },
      {
        headerName: "Credit Authorizations",
        field: "creditAuthorizations",
        hide: true,
      },
      {
        headerName: "Discount Points",
        field: "discountPoints",
        type: "percent",
        hide: true,
      },
      { headerName: "Dutch Interest", field: "dutchInterest", hide: true },
      {
        headerName: "Existing Loan In Default",
        field: "existingLoanInDefault",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Funded Date",
        field: "fundedDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Interest Rate",
        field: "interestRate",
        type: "percent",
        hide: true,
      },
      {
        headerName: "Is Balance Sheet Closing",
        field: "isBalanceSheetClosing",
        hide: true,
      },
      {
        headerName: "Is Table Funding",
        field: "isTableFunding",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Last Viewed",
        field: "lastViewDateTime",
        type: "date",
        minWidth: 200,
        hide: true,
      },

      { headerName: "Loan Documents", field: "loanDocuments", hide: true },
      {
        headerName: "Maturity Date",
        field: "maturityDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Milestone Status Changes",
        field: "milestoneStatusChanges",
        hide: true,
      },
      {
        headerName: "Net Funding",
        field: "netFunding",
        type: "dollar",
        hide: true,
      },
      {
        headerName: "Origination Date",
        field: "originationDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Origination Points",
        field: "originationPoints",
        type: "percent",
        hide: true,
      },
      { headerName: "Permitted Users", field: "permittedUsers", hide: true },
      {
        headerName: "Pipeline",
        field: "pipeline",
        width: 140,
        flex: 0,
        hide: true,
      },
      {
        headerName: "Referral Source",
        field: "referralSource",
        cellRenderer: CellRendererEditableLoanField,
        valueFormatter: (params) => params?.data?.referralSource || "None",
        hide: true,
      },
      {
        headerName: "Subject Properties",
        field: "subjectProperties",
        hide: true,
        valueGetter: (params) => {
          const v = params.data?.subjectProperties;
          if (isEmpty(v) || isNil(v)) return;
          return v.map((item) =>
            isEmpty(item) || isNil(item?.address?.fullAddress)
              ? "N/A"
              : `${item.address.fullAddress}`,
          );
        },
      },
    ],
    [showFavorites],
  );

  const getRowId = useCallback((params) => params.data._id, []);
  const getRowStyle = useCallback((params) => {
    if (params.node.footer) {
      return { fontWeight: "bold" };
    }
    return null;
  }, []);
  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      showRowGroup: false,
      sortable: true,
      flex: 1,
      resizable: true,
      cellClass: "ag-cell-vertical-center",
      minWidth: 200,
      floatingFilter: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      filter: "agMultiColumnFilter",
      filterParams: {},
    }),
    [],
  );

  const handleOnRowClicked = useCallback(
    (params) => {
      if (params.node.group) {
        params.node.setExpanded(!params.node.expanded);
        return;
      }
      onRowClicked?.(params?.data);
    },
    [onRowClicked],
  );

  const columnTypes = useMemo(
    () => ({
      ...columnTypesDate(),
      ...columnTypesNumber(),
      ...columnTypesDollar(),
    }),
    [],
  );
  const sideBar = useMemo(() => ({ toolPanels: ["columns", "filters"] }), []);
  const gridContext = useMemo(
    () => ({ toggleFavorite, isFavorite }),
    [toggleFavorite, isFavorite],
  );

  return (
    <Box
      sx={{
        borderRadius: "0px",
        height: "100%",
        ".ag-theme-quartz .ag-root-wrapper": {
          borderLeft: "none !important",
          borderRight: "none !important",
          borderBottom: "none !important",
          borderRadius: "0px !important",
        },
      }}
    >
      <AgGridTableWrapper height="100%" compactTable>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          getRowId={getRowId}
          quickFilterText={quickFilter}
          defaultColDef={defaultColDef}
          sideBar={sideBar}
          getRowStyle={getRowStyle}
          grandTotalRow={showGrandTotal ? "bottom" : undefined}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          onCellClicked={onCellClicked}
          loading={loading}
          groupDisplayType={"singleColumn"}
          onRowClicked={handleOnRowClicked}
          onGridReady={onGridReady}
          context={gridContext}
          tooltipShowDelay={100}
          headerHeight={54}
          floatingFiltersHeight={40}
          enableCellTextSelection
        />
      </AgGridTableWrapper>
    </Box>
  );
};

const MyLoansCheckbox = ({ activeView, onViewChange, assignedCount }) => {
  const isChecked = activeView === "assigned";
  return (
    <ButtonBase
      disableRipple
      onClick={() => onViewChange(isChecked ? "all" : "assigned")}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        userSelect: "none",
        borderRadius: "6px",
        px: 1,
        py: 1,
        border: "1px solid transparent",
        "&:hover": { bgcolor: "#f0f4f8" },
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: "4px",
          border: isChecked ? "2px solid #1a56db" : "2px solid #d1d5db",
          bgcolor: isChecked ? "#1a56db" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.12s",
        }}
      >
        {isChecked && (
          <Box component="svg" viewBox="0 0 12 12" sx={{ width: 9, height: 9 }}>
            <path
              d="M2 6l3 3 5-5"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          color: "#374151",
          whiteSpace: "nowrap",
          lineHeight: 1,
        }}
      >
        Show only my loans
        <Typography
          component="span"
          sx={{ fontSize: 12, color: "#94a3b8", pl: 0.4, lineHeight: 1 }}
        >
          ({assignedCount})
        </Typography>
      </Typography>
    </ButtonBase>
  );
};

const DownloadDropdownButton = () => {
  const { loading, downloadStageDurationReport, downloadPipeline } =
    useLoanFilesHook();

  const downloadAnchorRef = useRef(null);
  const downloadMenuRef = useRef(null);

  const handleDownload = useCallback(
    (type) => {
      downloadMenuRef.current?.close();
      if (type === "stage-duration") {
        downloadStageDurationReport({ fileDownloadName: "Pipeline Files" });
      } else if (type === "pipeline-zip") {
        downloadPipeline({ fileDownloadName: "Pipeline Files" });
      }
    },
    [downloadStageDurationReport, downloadPipeline],
  );

  return (
    <>
      <Button
        ref={downloadAnchorRef}
        onClick={() => downloadMenuRef.current?.toggle()}
        size="medium"
        variant="outlined"
        color="primary"
        loading={loading}
        endIcon={<KeyboardArrowDown />}
      >
        Export
      </Button>
      <StyledMenu
        ref={downloadMenuRef}
        anchorRef={downloadAnchorRef}
        title="Export Options"
        minWidth={260}
      >
        <Box sx={{ py: 0.5 }}>
          <MenuItem
            sx={{ py: 1, px: 1.5, mx: 0.75, borderRadius: "8px" }}
            onClick={() => handleDownload("stage-duration")}
          >
            <ListItemText
              primary={
                <Flx ac g={1}>
                  <DownloadRounded sx={{ fontSize: 16, color: "#6b7280" }} />
                  <Typography sx={{ fontSize: 12 }}>
                    Export Stage Duration Report to Excel
                  </Typography>
                </Flx>
              }
            />
          </MenuItem>
          <MenuItem
            sx={{ py: 1, px: 1.5, mx: 0.75, borderRadius: "8px" }}
            onClick={() => handleDownload("pipeline-zip")}
          >
            <ListItemText
              primary={
                <Flx ac g={1}>
                  <DownloadOutlined sx={{ fontSize: 16, color: "#6b7280" }} />
                  <Typography sx={{ fontSize: 12 }}>Export To Excel</Typography>
                </Flx>
              }
            />
          </MenuItem>
        </Box>
      </StyledMenu>
    </>
  );
};
export default LoanOfficerPipelineScreen;
